import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Brain,
  Clock,
  FileDown,
  FileText,
  GitBranch,
  GitFork,
  Layers,
  Play,
  RefreshCw,
  CheckCircle2,
  Loader,
  XCircle,
} from 'lucide-react';
import SecurityScoreCard from '../SecurityScoreCard';
import type { FindingFilterType } from '../SecurityFindingsFilter';
import { SectionCard, EmptyState } from './DashboardWidgets';
import { RepoRadioBar } from './ProjectDashboard';
import { exportSentinelPdf, exportSentinelDocx } from '@/lib/exportSentinel';
import type { RepoSecurityResult } from '@/types';

// ── Types ─────────────────────────────────────────────────────
interface DeepAiAnalysisProps {
  sentinel: any;
  appId: string;
  appName: string;
  extractionStatus: { extractionStatus: string | null; extractionStep?: string; extractionProgress?: any; totalRepos?: number; completedRepos?: number; repoProgress?: Record<string, any> } | null;
  isSectionExtracting?: boolean;
  onRunAnalysis: () => void;
  isRunning: boolean;
  loading?: boolean;
  lastScanDate?: string | null;
  onWorkflow: () => void;
  selectedRepo: string | null;
  onRepoChange: (slug: string) => void;
  repoProgress?: Record<string, any>;
  repos?: { slug: string; branch: string; url?: string; category?: string }[];
  repoResults: Record<string, RepoSecurityResult>;
  repoLoading: Record<string, boolean>;
}

// ── Component ─────────────────────────────────────────────────
export function DeepAiAnalysis({
  sentinel, appId, appName, extractionStatus, isSectionExtracting, onRunAnalysis, isRunning, loading,
  lastScanDate, onWorkflow,
  selectedRepo, onRepoChange, repoProgress, repos: reposProp,
  repoResults, repoLoading,
}: DeepAiAnalysisProps) {
  const theme = useTheme();
  const isExtracting = isSectionExtracting ?? extractionStatus?.extractionStatus === 'running';
  const hasSentinel = !!sentinel;
  const securityScore = sentinel?.aggregatedSecurityScore || sentinel?.securityScore;

  // State for SecurityScoreCard
  const [findingsFilter, setFindingsFilter] = useState<FindingFilterType>('ALL');
  const [expandedFindingPanel, setExpandedFindingPanel] = useState<string | false>(false);
  const handleFindingAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFindingPanel(isExpanded ? panel : false);
  };

  // Per-repo state — results/loading are fetched and cached by the parent (ProjectDashboard)
  // so the tab-strip score chip and this tab's content always share the same data.
  const repoManifest = sentinel?.repoManifest || [];
  const isMultiRepo = repoManifest.length > 1;

  const activeRepoData = selectedRepo ? repoResults[selectedRepo] : null;

  // Per-repo scanning: true if the currently selected repo is being scanned
  const isSelectedRepoScanning = isExtracting && !!selectedRepo && (
    repoProgress
      ? ['running', 'queued', 'pending'].includes(repoProgress[selectedRepo]?.status)
      : false
  );

  // ── Action Bar ─────────────────────────────────────────────
  const actionBar = (
    <Paper
      elevation={0}
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 1, px: 2, py: 1.25,
        borderRadius: 2, border: '1px solid', borderColor: 'divider',
        mb: 3,
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {isExtracting && (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mr: 1 }}>
            <Box sx={{
              width: 6, height: 6, borderRadius: '50%', bgcolor: '#3b82f6',
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
            }} />
            <Typography sx={{ fontSize: 11, color: 'primary.main', fontWeight: 500 }}>
              {extractionStatus?.extractionStep === 'analyzing' && extractionStatus?.extractionProgress
                ? `Analyzing (${extractionStatus.extractionProgress.completed || 0}/${extractionStatus.extractionProgress.total || '?'})` 
                : extractionStatus?.extractionStep === 'analyzing' && extractionStatus?.totalRepos
                ? `Scanning repos (${extractionStatus.completedRepos || 0}/${extractionStatus.totalRepos})` 
                : extractionStatus?.extractionStep === 'checkout' ? 'Checking out code...'
                : extractionStatus?.extractionStep === 'storing' ? 'Saving results...'
                : 'Scanning...'}
            </Typography>
          </Stack>
        )}
        {!isExtracting && (activeRepoData?.extractedAt || lastScanDate) && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.disabled', mr: 1 }}>
            <Clock size={13} />
            <Typography sx={{ fontSize: 11 }}>
              {new Date(activeRepoData?.extractedAt || lastScanDate!).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Stack>
        )}
      </Stack>

      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {sentinel && (
          <>
            <Tooltip title="Download PDF">
              <IconButton size="small" onClick={() => { try { exportSentinelPdf(sentinel, appName); } catch (err: any) { console.error('PDF export failed:', err); } }}
                sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
                <FileDown size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download DOCX">
              <IconButton size="small" onClick={() => exportSentinelDocx(sentinel, appName).catch(() => {})}
                sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
                <FileText size={16} />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Tooltip title="Integrate with Pipeline">
          <Button variant="outlined" size="small" startIcon={<GitBranch size={14} />} onClick={onWorkflow}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            Pipeline
          </Button>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          startIcon={isRunning ? <CircularProgress size={14} color="inherit" /> : <RefreshCw size={14} />}
          onClick={onRunAnalysis}
          disabled={isRunning || isExtracting}
          disableElevation
          sx={{
            textTransform: 'none', fontWeight: 700,
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' },
          }}
        >
          {isRunning ? 'Starting...' : sentinel ? 'Re-scan' : 'Run Scan'}
        </Button>
      </Stack>
    </Paper>
  );

  // ── Empty state: no sentinel data and not extracting ─────
  if (!hasSentinel && !isExtracting && !isSelectedRepoScanning) {
    return (
      <Stack spacing={3}>
        {actionBar}
        <EmptyState
          icon={<Brain size={28} />}
          title="Deep AI Analysis"
          description="Run a comprehensive AI-powered security analysis across all repositories. This scans source code, dependencies, and configurations using LLM-based analysis."
          action={
            <Button
              variant="contained"
              size="large"
              startIcon={<Play size={16} />}
              onClick={onRunAnalysis}
              disabled={isRunning}
              disableElevation
              sx={{
                borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 4, py: 1.25,
                background: '#0B6BCB',
                '&:hover': { background: '#185ABC' },
                '&.Mui-disabled': { background: '#0B6BCB', color: '#fff', opacity: 0.65 },
              }}
            >
              Run Deep Analysis
            </Button>
          }
        />
      </Stack>
    );
  }

  // ── Per-repo scanning card ─────────────────────────────
  const scanningCard = isSelectedRepoScanning ? (
    <SectionCard title={`${selectedRepo} — Security Score`} icon={<Brain size={16} />}>
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Box sx={{
          width: 56, height: 56, borderRadius: 3, mx: 'auto', mb: 3,
          bgcolor: alpha('#0B6BCB', 0.1),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'pulse 2s ease-in-out infinite',
          '@keyframes pulse': { '0%, 100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.7, transform: 'scale(0.95)' } },
        }}>
          <Brain size={28} color="#0B6BCB" />
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>Analysis in Progress</Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3 }}>
          AI is analyzing <strong>{selectedRepo}</strong>. Select another repository to view its last scan data.
        </Typography>
        <Stack spacing={1.5} sx={{ maxWidth: 380, mx: 'auto', textAlign: 'left' }}>
          {[
            { step: 'checkout', label: 'Checking out repositories', icon: <Layers size={14} /> },
            { step: 'analyzing', label: 'Scanning & analyzing code', icon: <Brain size={14} /> },
            { step: 'storing', label: 'Saving results', icon: <FileText size={14} /> },
          ].map(({ step, label, icon }) => {
            const currentStep = extractionStatus?.extractionStep || '';
            const steps = ['checkout', 'analyzing', 'storing'];
            const currentIdx = steps.indexOf(currentStep);
            const stepIdx = steps.indexOf(step);
            const isActive = currentStep === step;
            const isDone = stepIdx < currentIdx;
            return (
              <Stack key={step} direction="row" spacing={1.5} alignItems="center" sx={{ opacity: isDone || isActive ? 1 : 0.3 }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: 1.5,
                  bgcolor: isDone ? alpha('#22c55e', 0.1) : isActive ? alpha('#0B6BCB', 0.1) : 'action.hover',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: isDone ? '#22c55e' : isActive ? '#0B6BCB' : 'text.disabled',
                }}>
                  {isDone ? <CheckCircle2 size={14} /> : isActive ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? 'text.primary' : 'text.secondary' }}>
                    {label}
                  </Typography>
                  {isActive && extractionStatus?.totalRepos && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography sx={{ fontSize: 10, color: 'text.disabled', mb: 0.5 }}>
                        {extractionStatus.completedRepos || 0} of {extractionStatus.totalRepos} repos
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={((extractionStatus.completedRepos || 0) / extractionStatus.totalRepos) * 100}
                        sx={{ height: 3, borderRadius: 2, bgcolor: alpha('#0B6BCB', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#0B6BCB' } }}
                      />
                    </Box>
                  )}
                </Box>
              </Stack>
            );
          })}
        </Stack>
      </Box>
    </SectionCard>
  ) : null;

  // ── Results available — show the original Sentinel metrics ──
  return (
    <Stack spacing={3}>
      {actionBar}

      {/* Repo selector is now rendered by the parent (ProjectDashboard) for all AI Insights sub-tabs */}

      {/* First-scan step card: scan running, no per-repo data loaded yet for this repo */}
      {isExtracting && !activeRepoData?.securityScore && !(selectedRepo && repoLoading[selectedRepo]) && (
        <SectionCard title="Deep AI Scan in Progress" icon={<Brain size={16} />}>
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Box sx={{
              width: 56, height: 56, borderRadius: 3, mx: 'auto', mb: 3,
              bgcolor: alpha('#0B6BCB', 0.1),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': { '0%, 100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.7, transform: 'scale(0.95)' } },
            }}>
              <Brain size={28} color="#0B6BCB" />
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>Analysis in Progress</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3 }}>
              AI is scanning all repositories using LLM-based security analysis...
            </Typography>
            <Stack spacing={1.5} sx={{ maxWidth: 380, mx: 'auto', textAlign: 'left' }}>
              {[
                { step: 'checkout', label: 'Checking out repositories', icon: <Layers size={14} /> },
                { step: 'analyzing', label: 'Scanning & analyzing code', icon: <Brain size={14} /> },
                { step: 'storing', label: 'Saving results', icon: <FileText size={14} /> },
              ].map(({ step, label, icon }) => {
                const currentStep = extractionStatus?.extractionStep || '';
                const steps = ['checkout', 'analyzing', 'storing'];
                const currentIdx = steps.indexOf(currentStep);
                const stepIdx = steps.indexOf(step);
                const isActive = currentStep === step;
                const isDone = stepIdx < currentIdx;
                return (
                  <Stack key={step} direction="row" spacing={1.5} alignItems="center" sx={{ opacity: isDone || isActive ? 1 : 0.3 }}>
                    <Box sx={{
                      width: 28, height: 28, borderRadius: 1.5,
                      bgcolor: isDone ? alpha('#22c55e', 0.1) : isActive ? alpha('#0B6BCB', 0.1) : 'action.hover',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: isDone ? '#22c55e' : isActive ? '#0B6BCB' : 'text.disabled',
                    }}>
                      {isDone ? <CheckCircle2 size={14} /> : isActive ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? 'text.primary' : 'text.secondary' }}>
                        {label}
                      </Typography>
                      {isActive && extractionStatus?.totalRepos && (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography sx={{ fontSize: 10, color: 'text.disabled', mb: 0.5 }}>
                            {extractionStatus.completedRepos || 0} of {extractionStatus.totalRepos} repos
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={((extractionStatus.completedRepos || 0) / extractionStatus.totalRepos) * 100}
                            sx={{ height: 3, borderRadius: 2, bgcolor: alpha('#0B6BCB', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#0B6BCB' } }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        </SectionCard>
      )}

      {/* Security Score Card — always sourced from per-repo data only, never from aggregated sentinel */}
      {(selectedRepo || hasSentinel) && (
        <>
          {isSelectedRepoScanning ? scanningCard : (
            <SectionCard
              title={selectedRepo ? `${selectedRepo} — Security Score` : 'Security Score'}
              icon={<Brain size={16} />}
            >
              {/* No repo selected yet */}
              {!selectedRepo && (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <GitFork size={22} color={theme.palette.text.disabled} />
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1 }}>
                    Select a repository above to view its security details
                  </Typography>
                </Box>
              )}
              {/* Per-repo data loading */}
              {selectedRepo && repoLoading[selectedRepo] && (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <CircularProgress size={24} thickness={4} sx={{ mb: 1.5 }} />
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                    Loading {selectedRepo}…
                  </Typography>
                </Box>
              )}
              {/* Per-repo data loaded but no security score available */}
              {selectedRepo && !repoLoading[selectedRepo] && !activeRepoData?.securityScore && (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <XCircle size={22} color={theme.palette.text.disabled} />
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1 }}>
                    No data available for {selectedRepo}
                  </Typography>
                </Box>
              )}
              {/* Per-repo security score — the only data source, no aggregated fallback */}
              {selectedRepo && !repoLoading[selectedRepo] && activeRepoData?.securityScore && (
                <SecurityScoreCard
                  score={activeRepoData.securityScore}
                  appId={appId}
                  findingsFilter={findingsFilter}
                  setFindingsFilter={setFindingsFilter}
                  expandedFindingPanel={expandedFindingPanel}
                  onFindingAccordionChange={handleFindingAccordionChange}
                  threatModelDiagram={activeRepoData.threatModelSummary?.diagram || (activeRepoData.threatModelDiagram as string | undefined)}
                  threatFindings={activeRepoData.threatModelSummary?.findings || []}
                />
              )}
            </SectionCard>
          )}
        </>
      )}

      {/* Executive Summary — per-repo only, no aggregated fallback */}
      {activeRepoData?.securityScore?.summary && (
        <SectionCard title="Executive Summary" icon={<Brain size={16} />}>
          <Box sx={{
            p: 2, borderRadius: 2,
            bgcolor: alpha('#0B6BCB', 0.03),
            border: '1px solid', borderColor: alpha('#0B6BCB', 0.1),
          }}>
            <Typography sx={{ fontSize: 13, color: 'text.primary', lineHeight: 1.7 }}>
              {activeRepoData.securityScore.summary}
            </Typography>
          </Box>
        </SectionCard>
      )}

      {/* Engineering Recommendations — per-repo only, no aggregated fallback */}
      {activeRepoData?.securityScore?.topRecommendations &&
       activeRepoData.securityScore.topRecommendations.length > 0 && (
        <SectionCard title="Engineering Recommendations" icon={<Brain size={16} />}>
          <Stack spacing={1}>
            {activeRepoData.securityScore.topRecommendations.map((rec: string, idx: number) => (
              <Stack key={idx} direction="row" spacing={1.5} alignItems="flex-start" sx={{
                p: 1.5, borderRadius: 1.5,
                bgcolor: alpha('#0B6BCB', 0.03),
                border: '1px solid', borderColor: alpha('#0B6BCB', 0.1),
              }}>
                <Box sx={{
                  width: 22, height: 22, borderRadius: 1, flexShrink: 0,
                  bgcolor: alpha('#0B6BCB', 0.1),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#0B6BCB', fontSize: 11, fontWeight: 700,
                }}>
                  {idx + 1}
                </Box>
                <Typography sx={{ fontSize: 12, color: 'text.primary', lineHeight: 1.6 }}>
                  {rec}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </SectionCard>
      )}
    </Stack>
  );
}
